import React, { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../context/globalContext";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { NavLink } from "react-router-dom";
import eventLogo from "../../assets/media/logos/company/GWSCE-logo.png";
import { updateDietaryRequirement } from "../../api/product";
import { TicketType } from "../../api/events";
import ConferenceRateTable from "./ConferenceRateTable";
import TicketRateTable from "./TicketRateTable";

interface EventContent {
  eventName: any;
  tags: string[];
  img: any;
  basicInfo: {
    eventDescriptionShort: string[];
    eventDescriptionLong: string[];
    date: string;
    time?: string;
    location: string;
    status: string;
    link?: string;
    email?: string;
  };
  keynoteInfo?: {
    name: string;
    desc: string;
    keynoteTitle: string;
  }[];
  pricingInfo?: {
    name: string;
    variantId: string;
    price: number;
  }[];
  contactInfo: {
    email: string;
    phone?: string;
    office?: string;
    mapImg: any;
  };
  eventSlug?: string;
  registrationMode?: "tickets" | "invitation" | "conference";
  ticketTypes?: TicketType[];
  pricingRefreshKey?: number;
}

interface KeynoteProps {
  keynoteInfo?: {
    name: string;
    desc: string;
    keynoteTitle: string;
  }[];
}

interface ContactProps {
  contactInfo: {
    email: string;
    phone?: string;
    office?: string;
    mapImg: any;
  };
}

interface BasicProps {
  basicInfo: {
    eventDescriptionShort: string[];
    eventDescriptionLong: string[];
    date: string;
    time?: string;
    location: string;
    status: string;
    link?: string;
    email?: string;
  };
}

interface InvitationRegistrationProps {
  eventSlug?: string;
  registrationOpen?: boolean;
}

const EventPage: React.FC<EventContent> = ({
  eventName,
  tags,
  img,
  basicInfo,
  keynoteInfo,
  pricingInfo,
  contactInfo,
  eventSlug,
  registrationMode = eventSlug ? "tickets" : "invitation",
  ticketTypes,
  pricingRefreshKey = 0,
}) => {
  const basicInfoRef = useRef(null);
  const contactInfoRef = useRef(null);
  const pricingRef = useRef(null);
  const keynoteRef = useRef(null);

  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let isAnySectionIntersecting = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            isAnySectionIntersecting = true;
          }
        });
        if (!isAnySectionIntersecting) {
          setActiveSection(null);
        }
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    // Observing each section
    const sections = [basicInfoRef, contactInfoRef, pricingRef, keynoteRef];
    sections.forEach((section) => {
      if (section.current) {
        observer.observe(section.current);
      }
    });

    // Cleanup observer on unmount
    return () => {
      sections.forEach((section) => {
        if (section.current) {
          observer.unobserve(section.current);
        }
      });
    };
  }, []);

  const scrollToRef = (ref: any) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  const { currentUser } = useContext(GlobalContext);

  const isConference = registrationMode === "conference";
  const usesPaidRegistration =
    (registrationMode === "conference" || registrationMode === "tickets") &&
    Boolean(eventSlug);
  const isClosed = basicInfo.status === "Closed";
  const showWeBridgeBranding = eventSlug === "gwsce-2025";

  return (
    <div className="max-w-[920px] mx-auto text-slate-800 flex flex-col items-center mb-32">
      <div className="flex flex-col w-full">
        <div className="flex flex-col w-full mb-6">
          {basicInfo.status && (
            <span className="inline-block self-start text-xs font-medium uppercase tracking-wide text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-1 mb-4">
              {basicInfo.status}
            </span>
          )}
          <h1 className="font-bold text-3xl sm:text-4xl text-slate-900 leading-tight">
            {eventName}
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            {basicInfo.eventDescriptionShort}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {tags.map((i) => (
              <span
                key={"tag-" + i}
                className="bg-slate-100 text-xs font-medium text-slate-700 py-1 px-2.5 border border-slate-200"
              >
                {i}
              </span>
            ))}
          </div>
          {!isClosed && (
            <div className="mt-8">
              {usesPaidRegistration ? (
                <NavLink
                  to={`/events/${eventSlug}/register`}
                  className="eh-btn-primary"
                >
                  Register for this event
                </NavLink>
              ) : (
                <button
                  onClick={() => scrollToRef(pricingRef)}
                  className="eh-btn-primary"
                  type="button"
                >
                  {currentUser && currentUser.dietaryRequirement
                    ? "Registered"
                    : "Register"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="relative h-[280px] sm:h-[360px] w-full overflow-hidden border border-slate-200">
          <img
            className="w-full h-full object-cover"
            src={img}
            alt=""
          />
          {(showWeBridgeBranding || isConference) && (
            <div
              className={`absolute inset-0 ${
                isConference
                  ? "bg-gradient-to-t from-slate-900/50 to-transparent"
                  : "bg-black/60"
              }`}
            />
          )}
          {showWeBridgeBranding && (
            <div className="w-full h-full absolute top-0 left-0 flex flex-col gap-4 items-center justify-center">
              <img className="w-[240px] max-w-[70%]" src={eventLogo} alt="" />
              <p className="text-white/80 text-sm">
                Organised by{" "}
                <a
                  href="https://windenergybridge.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline"
                >
                  WeBridge
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
      <Nav
        activeSection={activeSection}
        onBasicInfoClick={() => scrollToRef(basicInfoRef)}
        onPricingClick={() => scrollToRef(pricingRef)}
      />
      <div className="flex flex-col gap-20 w-full">
        <div id="pricingInfo" ref={pricingRef}>
          {registrationMode === "conference" && eventSlug ? (
            <ConferenceRateTable
              key={pricingRefreshKey}
              eventSlug={eventSlug}
              registrationOpen={!isClosed}
            />
          ) : registrationMode === "tickets" && eventSlug ? (
            <TicketRateTable
              eventSlug={eventSlug}
              ticketTypes={ticketTypes}
              registrationOpen={!isClosed}
            />
          ) : (
            <InvitationRegistration
              eventSlug={eventSlug}
              registrationOpen={!isClosed}
            />
          )}
        </div>
        <div id="basicInfo" ref={basicInfoRef}>
          <Basic basicInfo={basicInfo} />
        </div>
        {/* <div id="keynoteInfo" ref={keynoteRef}>
          <Keynote keynoteInfo={keynoteInfo} />
        </div> */}

        {/* <div id="contactInfo" ref={contactInfoRef}>
          <Contact contactInfo={contactInfo} />
        </div> */}
      </div>
    </div>
  );
};

export default EventPage;

function Nav({
  activeSection,
  onBasicInfoClick,
  onPricingClick,
}: {
  activeSection: string | null;
  onBasicInfoClick: () => void;
  onPricingClick: () => void;
}) {
  return (
    <div className="sticky top-header z-10 w-full mb-6 border-b border-slate-200 bg-slate-50/95 backdrop-blur">
      <nav className="flex gap-8 py-3 text-sm font-medium">
        <button
          type="button"
          onClick={onPricingClick}
          className={`pb-3 -mb-px border-b-2 transition-colors ${
            activeSection === "pricingInfo"
              ? "border-slate-800 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          Registration
        </button>
        <button
          type="button"
          onClick={onBasicInfoClick}
          className={`pb-3 -mb-px border-b-2 transition-colors ${
            activeSection === "basicInfo"
              ? "border-slate-800 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          About
        </button>
      </nav>
    </div>
  );
}

function Keynote({ keynoteInfo }: KeynoteProps) {
  return (
    <div>
      <SectionHeader
        subtitle="Highlight"
        header="Keynote Speakers"
        description="Learn about our keynote speakers and their topics of discussion."
      />
      {keynoteInfo && (
        <div className="flex flex-col gap-4 border-b border-slate-700">
          {keynoteInfo.map((info, index) => (
            <div key={index} className="py-4 border-t border-slate-700">
              <div className="flex gap-3 items-center mb-3">
                <h3 className="font-bold text-xl">{info.name}</h3>
                <h4 className="italic">{info.keynoteTitle}</h4>
              </div>
              <p>{info.desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Contact({ contactInfo }: ContactProps) {
  return (
    <div>
      <SectionHeader
        subtitle="Connect"
        header="Get in Touch"
        description="Have any questions or need assistance? Contact our support team."
      />
      <div className="flex justify-between mt-20 gap-10">
        <div className="flex flex-col gap-10 h-full justify-between">
          <div>
            <span className="material-symbols-outlined">mail</span>
            <p className="font-bold text-lg">Email</p>
            <p>{contactInfo.email}</p>
          </div>
          {contactInfo.phone && (
            <div>
              <span className="material-symbols-outlined">phone_in_talk</span>
              <p className="font-bold text-lg">Phone</p>
              <p>{contactInfo.phone}</p>
            </div>
          )}
          {contactInfo.office && (
            <div>
              <span className="material-symbols-outlined">pin_drop</span>
              <p className="font-bold text-lg">Venue</p>
              <p>{contactInfo.office}</p>
            </div>
          )}
        </div>
        <img
          className="max-w-[60%] object-cover"
          src={contactInfo.mapImg}
          alt=""
        />
      </div>
    </div>
  );
}

function Basic({ basicInfo }: BasicProps) {
  return (
    <div className="eh-card">
      <SectionHeader
        subtitle="Overview"
        header="About this event"
        description=""
      />
      <div className="flex flex-col gap-8">
        <div>
          {basicInfo.eventDescriptionLong.map((i) => (
            <p key={"event-desc-long-" + i} className="mb-4 text-slate-600 leading-relaxed">
              {i}
            </p>
          ))}
        </div>
        <ul className="border border-slate-200 divide-y divide-slate-200 text-sm">
            <li className="flex justify-between py-3 px-4">
              <span className="font-medium text-slate-700">Date</span>
              <span className="text-slate-900">{basicInfo.date}</span>
            </li>
            {basicInfo.time && (
              <li className="flex justify-between py-3 px-4">
                <span className="font-medium text-slate-700">Time</span>
                <span className="text-slate-900">{basicInfo.time}</span>
              </li>
            )}
            <li className="flex justify-between py-3 px-4">
              <span className="font-medium text-slate-700">Location</span>
              <span className="text-slate-900 text-right max-w-[60%]">
                {basicInfo.location}
              </span>
            </li>
            <li className="flex justify-between py-3 px-4">
              <span className="font-medium text-slate-700">Registration</span>
              <span className="text-slate-900">{basicInfo.status}</span>
            </li>
            {basicInfo.link && (
              <li className="flex flex-col sm:flex-row justify-between gap-2 py-3 px-4">
                <span className="font-medium text-slate-700">Website</span>
                <span className="text-slate-900 break-all">
                  <a
                    href={basicInfo.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-slate-700"
                  >
                    {basicInfo.link}
                  </a>
                </span>
              </li>
            )}
            {basicInfo.email && (
              <li className="flex flex-col sm:flex-row justify-between gap-2 py-3 px-4">
                <span className="font-medium text-slate-700">Contact</span>
                <span className="text-slate-900">
                  <a
                    href={`mailto:${basicInfo.email}`}
                    className="underline hover:text-slate-700"
                  >
                    {basicInfo.email}
                  </a>
                </span>
              </li>
            )}
          </ul>
      </div>
    </div>
  );
}

function InvitationRegistration({
  eventSlug: _eventSlug,
  registrationOpen = true,
}: InvitationRegistrationProps) {
  const { currentUser, setCustomAlert, setCurrentUser } =
    useContext(GlobalContext);
  const [invitationCode, setInvitationCode] = useState<string>("");
  const [dietaryRequirement, setDietaryRequirement] = useState<string>("None");
  const [otherDietaryRequirement, setOtherDietaryRequirement] =
    useState<string>("");
  const [isRegistering, setIsRegistering] = useState<boolean>(false);

  function closeAlert() {
    setCustomAlert(null);
  }

  function handleVerifyInvitation() {
    if (!currentUser || Object.keys(currentUser).length === 0) {
      setCustomAlert(
        <div>
          <p className="font-bold mb-2">Login Required</p>
          <p>
            Please{" "}
            <NavLink to="login" className="underline" onClick={closeAlert}>
              log in
            </NavLink>{" "}
            to proceed with the registration.
          </p>
          <p className="text-sm mt-4 opacity-60">
            If you don't have an account, please{" "}
            <NavLink to="signup" className="underline" onClick={closeAlert}>
              sign up
            </NavLink>{" "}
            to continue.
          </p>
        </div>
      );
      return;
    }
    if (invitationCode !== "GWSCE2025") {
      setCustomAlert(
        <div>
          <p className="font-bold mb-2">Wrong Invitation Code</p>
          <p>Please check the invitation code and try again.</p>
          <p className="text-sm mt-4 opacity-60">
            If you continue to experience issues, contact support for
            assistance.
          </p>
        </div>
      );
      return;
    }
    if (
      dietaryRequirement === "Other" &&
      otherDietaryRequirement.trim() === ""
    ) {
      setCustomAlert(
        <div>
          <p className="font-bold mb-2">Incomplete Information</p>
          <p>Please specify your dietary requirement.</p>
          <p className="text-sm mt-4 opacity-60">
            If you continue to experience issues, contact support for
            assistance.
          </p>
        </div>
      );
      return;
    }
    setIsRegistering(true);
    updateDietaryRequirement(
      currentUser.id,
      dietaryRequirement === "Other"
        ? `other-${otherDietaryRequirement}`
        : dietaryRequirement
    )
      .then((data) => {
        setCurrentUser((prevUser: any) => ({
          ...prevUser,
          dietaryRequirement:
            dietaryRequirement === "Other"
              ? `other-${otherDietaryRequirement}`
              : dietaryRequirement,
        }));
        setInvitationCode("");
        setDietaryRequirement("None");
        setOtherDietaryRequirement("");

        setCustomAlert(
          <div>
            <p className="font-bold mb-2">Registration Successful</p>
            <p>Your registration has been completed successfully.</p>
            <p className="text-sm mt-4 opacity-60">
              You will receive a confirmation email shortly.
            </p>
          </div>
        );
      })
      .catch((err) => {
        setCustomAlert(
          <div>
            <p className="font-bold mb-2">Registration Failed</p>
            <p>There was an error processing your registration.</p>
            <p className="text-sm mt-4 opacity-60">
              Please try again later or contact support for assistance.
            </p>
          </div>
        );
      })
      .finally(() => {
        setIsRegistering(false);
      });
  }

  return (
    <div>
      <SectionHeader
        subtitle=""
        header="Register"
        description={
          currentUser && currentUser.dietaryRequirement
            ? ""
            : "Provide your information to register for the event."
        }
      />
      <div className=" w-full py-5 pt-0 -mt-5">
        {!registrationOpen ? (
          <p className="text-slate-600">Registration for this event is closed.</p>
        ) : currentUser && currentUser.dietaryRequirement ? (
          <p>You have successfully registered for this event.</p>
        ) : (
          <div className="flex flex-col text-sm sm:text-base border-t border-slate-700 py-5">
            {/* <p className="font-bold">Invitation Only</p> */}
            <div className="text-left h-full w-full relative flex flex-col sm:flex-row gap-2">
              <div className="mb-4 w-full">
                <label>
                  <p className="font-bold">Invitation Code *</p>
                  <input
                    type="text"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    placeholder="Enter code"
                    className="border h-[3rem] w-full text-[0.875rem] border-secondary-dark p-2 bg-white"
                  />
                </label>
              </div>
              <div className="mb-4 w-full flex flex-col gap-2">
                <div>
                  <label>
                    <p className="font-bold">Dietary Requirement *</p>
                    <select
                      value={dietaryRequirement}
                      onChange={(e) => setDietaryRequirement(e.target.value)}
                      className="border h-[3rem] w-full text-[0.875rem] border-secondary-dark p-2 bg-white"
                    >
                      <option value="None">None</option>
                      <option value="Vegetarian">Vegetarian</option>
                      <option value="Vegan">Vegan</option>
                      <option value="Gluten-Free">Gluten-Free</option>
                      <option value="Other">Other</option>
                    </select>
                  </label>
                </div>
                {dietaryRequirement === "Other" && (
                  <div>
                    <input
                      type="text"
                      value={otherDietaryRequirement}
                      onChange={(e) =>
                        setOtherDietaryRequirement(e.target.value)
                      }
                      placeholder="Specify Dietary Requirement *"
                      className="border h-[3rem] w-full text-[0.875rem] border-secondary-dark p-2 bg-white"
                    />
                  </div>
                )}
              </div>
            </div>
            <button
              className="mt-4 self-end whitespace-nowrap border btn rounded-none bg-white border-slate-700 text-slate-900 p-2 hover:bg-white hover:border-slate-800 hover:shadow-md"
              type="button"
              onClick={() => {
                handleVerifyInvitation();
              }}
              disabled={isRegistering}
            >
              {isRegistering ? "Registering..." : "Submit and Register"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
